import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, of, Subject } from 'rxjs';
import { concatMap, debounceTime, filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { DialogResult, PopupDialog, ToastService } from '@app/Common';
import { FormFlowFieldTypes, ListSelectTypes, ListTypes, ModuleUniqueIds } from '@app/Constants';
import { Permissions } from '@app/Models/Permissions';
import { TenantService, PermissionService } from '@app/Services';
import { FieldModalComponent } from '@app/Tenant/Applications/Airsweb/Modals/field-modal.component';
import { DefaultDataValue, Field, FieldGroup, FieldOrder, Patch, JsonChange } from '@app/Tenant/Applications/Airsweb/Models';
import { LocationAttribute } from '@app/Tenant/Applications/Airsweb/Models/location-attribute.model';
import { LocationAttributesService, ModuleService, ParamMapService } from '@app/Tenant/Applications/Airsweb/Services';
import { FieldService } from './field.service';
import { findFieldAliasConflict } from './field-alias-conflict';
import { JsonEditorComponent } from './json-editor/json-editor.component';
import { DynamicDefaultValueTokenOption, buildDynamicDefaultDataValue, getSelectedDynamicDefaultValueToken, getSupportedDynamicDefaultValueTokens } from './dynamic-default-value-token-option';
import { NgClass, AsyncPipe } from '@angular/common';
import { MatExpansionPanelTitle } from '@angular/material/expansion';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatTooltip } from '@angular/material/tooltip';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { FormsModule } from '@angular/forms';
import { TranslationPipe } from '../../../../../../Services/translation.pipe';
import { FieldGroupFilter } from '../../../../../../FieldGroupFilter';

@Component({
    selector: 'aw-fields',
    templateUrl: './fields.component.html',
    styleUrls: ['./fields.component.less'],
    imports: [MatExpansionPanelTitle, MatButton, MatIcon, NgClass, ExtendedModule, MatSlideToggle, MatTooltip, MatIconButton, MatMenuTrigger, MatMenu, MatMenuItem, FormsModule, AsyncPipe, TranslationPipe, FieldGroupFilter]
})
export class FieldsComponent implements OnInit, OnDestroy {
    public fieldFilter = '';
    public environmentalIndicatorDetailStep = 'EnvironmentalIndicatorDetail';
    public stepId = '';
    public orderKeyup = new Subject<any>();
    public newGroupErrorString: string;
    public addNewGroupIsDisabled = true;
    public canEditJson = false;

    private readonly destroy$ = new Subject<void>();
    private moduleId = '';
    private version = 1;
    private getMultiFields = false;
    private newGroupName: string;
    private groupListPrefix: string;
    private hierarchyAttributes: LocationAttribute[] = [];

    public constructor(private readonly activatedRoute: ActivatedRoute,
        public moduleService: ModuleService,
        private readonly tenantService: TenantService,
        private readonly permissionService: PermissionService,
        public dialog: MatDialog,
        public toast: ToastService,
        public fieldService: FieldService,
        public paramMapService: ParamMapService,
        private readonly locationAttributesService: LocationAttributesService,
        private readonly router: Router) { }

    public ngOnInit(): void {
        this.moduleId = this.paramMapService.getParam(this.activatedRoute, 'moduleId');
        this.groupListPrefix = `${this.moduleId.toLowerCase()}_group`;
        this.version = parseInt(this.paramMapService.getParam(this.activatedRoute, 'version'));
        this.canEditJson = this.permissionService.hasPermission(Permissions.jsonEditor);

        this.locationAttributesService
            .getHierarchyAttributes(this.tenantService.selectedTenant)
            .pipe(takeUntil(this.destroy$))
            .subscribe(result => this.hierarchyAttributes = result ?? []);

        this.activatedRoute.queryParams
            .subscribe(params => {
                this.getMultiFields = (params.getMultiFields === 'true');
            });

        combineLatest([
            this.activatedRoute.queryParams,
            this.moduleService.module()]
        )
            .pipe(takeUntil(this.destroy$))
            .subscribe(([queryParams, result]) => {
                this.stepId = queryParams.stepId;
                this.fieldFilter = queryParams.fieldFilter;

                if (!result) {
                    return;
                }

                if (this.getMultiFields) {
                    this.getStepMultiFields();
                }
                else {
                    this.getStepFields();
                }
            });

        this.orderKeyup
            .pipe(
                takeUntil(this.destroy$),
                debounceTime(500),
                switchMap((newOrderEvent: any) => {
                    const oldPosition = newOrderEvent.oldpos;
                    const newPosition = newOrderEvent.newpos;
                    const uiGroupName = newOrderEvent.group;
                    return of(this.moveField(uiGroupName, oldPosition, newPosition));
                }),
                filter((patches) => !!patches && patches.length > 0),
                concatMap((patches) => this.applyPatch(patches))
            ).subscribe();

        this.addNewGroupIsDisabled = true;
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
    }

    public trackByField(index: number, item: Field): string {
        return item.FieldId; // or item.id
    };

    public groupTrackBy(index: number, item: Field): number {
        return index;
    }

    public moveField(uiGroupName: string, oldPosition: number, newPosition: number): Patch[] {
        if (oldPosition === newPosition || !oldPosition || !newPosition) {
            return;
        }

        const groupedFields = this.getOrderedGroupItems(uiGroupName);
        this.moveMemberInArray(groupedFields, oldPosition - 1, newPosition - 1);
        this.reevaluateOrderOfFieldArray(groupedFields);

        this.moduleService.updateOrders(
            groupedFields,
            this.tenantService.selectedTenant,
            this.moduleId,
            this.version,
            this.stepId
        );

        return groupedFields.map(field => this.fieldToPatch(field));
    }

    public getChildRoute(route: ActivatedRoute, level: number): ActivatedRoute {
        if (level === 0) {
            return route;
        }

        route = route.firstChild;
        return this.getChildRoute(route, level - 1);
    }

    public configureLookup(stepId: string, fieldId: string): void {
        this.router.navigate(['field', fieldId], {
            relativeTo: this.getChildRoute(this.activatedRoute.root, 4),
            queryParams: {
                stepId,
                getMultiFields: this.getMultiFields
            }
        });
    }

    public configureLinkedRecord(stepId: string, field: Field): void {
        this.router.navigate(['field', field.FieldId, 'linkedrecordconfig'], {
            relativeTo: this.getChildRoute(this.activatedRoute.root, 4),
            queryParams: {
                stepId,
                getMultiFields: this.getMultiFields,
                linkedRecordModuleId: field.LinkedRecordModuleId
            }
        });
    }

    public getStepFields(): void {
        this.moduleService.getStepFields(this.stepId);
    }

    public getStepMultiFields(): void {
        this.moduleService.getStepMultiFields(this.stepId);
    }

    public addNewFieldGroup(): void {
        this.newGroupErrorString = '';
        if (!this.validateNewGroup()) {
            return;
        }

        // The group list is shared by every field group in this module, so it usually already
        // exists — only create it the first time, and use whichever key is actually correct
        // (the existing list's, or the one the backend just persisted) for the item add and
        // the group alias, rather than assuming groupListPrefix survived sanitisation unchanged.
        // Matched by FriendlyName, not Key: addList(groupListPrefix) stores groupListPrefix
        // verbatim as FriendlyName (only Key gets backend-sanitised), so this is the one field
        // guaranteed to still equal groupListPrefix exactly, regardless of what sanitisation
        // did to Key.
        this.tenantService.getLists().pipe(
            concatMap(lists => {
                const existingKey = lists.find(list => list.FriendlyName === this.groupListPrefix)?.Key;
                return existingKey
                    ? of(existingKey)
                    : this.tenantService.addList(this.groupListPrefix).pipe(map(newList => {
                        if (!newList?.Key) {
                            throw new Error('List created but no Key returned');
                        }
                        return newList.Key;
                    }));
            }),
            concatMap(listKey => this.tenantService
                .addListItem(ListTypes.AirswebList, listKey, this.newGroupName.trim())
                .pipe(map(newListItem => ({ listKey, newListItem })))),
            takeUntil(this.destroy$))
            .subscribe({
                next: ({ listKey, newListItem }) => {
                    const groupNameAliasWithModulePrefix = `${listKey}:${newListItem.Alias}`;
                    if (this.fieldGroupsAlreadyContainsNewGroup(groupNameAliasWithModulePrefix)) {
                        this.newGroupErrorString = 'Cannot add duplicate Group Names.';
                        this.addNewGroupIsDisabled = true;
                    }
                    else {
                        this.addFieldGroupToCurrentFieldGroupsForSelectedStep(groupNameAliasWithModulePrefix);
                    }
                },
                error: () => {
                    this.newGroupErrorString = 'Unable to add group. Please try again.';
                }
            });
    }

    public deleteGroupName(fieldGroupNameToDelete: string): void {
        if (!fieldGroupNameToDelete || fieldGroupNameToDelete.trim().length <= 0 || !this.canDeleteFieldGroup(fieldGroupNameToDelete)) {
            return;
        }

        const listItemAliasWithoutListAliasPrefix =
            this.removeModulePrefixFromGroupAlias(fieldGroupNameToDelete);

        this.tenantService.deleteListItem(ListTypes.AirswebList, this.moduleId.toLowerCase(), listItemAliasWithoutListAliasPrefix)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                if (this.getMultiFields) {
                    this.getStepMultiFields();
                } else {
                    this.getStepFields();
                }
            });
    }

    public canDeleteFieldGroup(fieldGroupNameToDelete: string): boolean {
        if (!fieldGroupNameToDelete || fieldGroupNameToDelete.trim().length <= 0) {
            return false;
        }

        return this.moduleService.fields.filter((field => field.GroupName === fieldGroupNameToDelete)).length <= 0;
    }

    public prettifyLabel(label: string): string {
        let prettifiedLabel: string;
        if (!label) {
            return '';
        }
        if (label.indexOf(':') > -1) {
            prettifiedLabel = label.split(':')[1];
        } else {
            prettifiedLabel = label;
        }

        return prettifiedLabel.replace(/_/g, ' ');
    }

    public updateOrder(sourceField: Field, destinationField: Field): void {
        const fieldOrder = new FieldOrder();
        fieldOrder.SourceFieldId = `${sourceField.GroupName}/${sourceField.FieldId}`;
        fieldOrder.SourceFieldOrder = sourceField.Order;
        fieldOrder.DestinationFieldId = `${destinationField.GroupName}/${destinationField.FieldId}`;
        fieldOrder.DestinationFieldOrder = destinationField.Order;

        this.moduleService.updateOrder(
            fieldOrder,
            this.tenantService.selectedTenant,
            this.moduleId,
            this.version,
            this.stepId
        );
    }

    public openFieldModalComponent(field: Field): void {
        const dialogRef = this.dialog.open(FieldModalComponent);
        dialogRef.componentInstance.setField(field);
        dialogRef.componentInstance.DialogTitle = this.prettifyLabel(field.FieldName);
    }

    public async editMatrixName(field: Field): Promise<void> {
        const fieldParsed = JSON.parse(field.Json);
        const all = await this.tenantService.getAllMatrices().toPromise();
        const dialogRef = this.dialog.open<PopupDialog, {}, DialogResult>(PopupDialog);
        const dialogInstance = dialogRef.componentInstance;
        dialogInstance.DialogTitle = 'Edit matrix name';
        dialogInstance.DialogConfirmButtonText = 'Save';
        dialogInstance.DialogCancelButtonText = 'Cancel';
        dialogInstance.DialogSelectedOption = fieldParsed.Control.Properties.MatrixName;
        dialogInstance.DialogOptions = all.map(m => m.Name);
        const matrixNameDialogResult = await dialogRef.afterClosed().toPromise();

        if (!matrixNameDialogResult || !matrixNameDialogResult.Result) {
            return;
        }

        const newField = {
            ...field,
            Modified: true,
            Json: JSON.stringify({
                ...fieldParsed,
                Control: {
                    ...fieldParsed.Control,
                    Properties: {
                        ...fieldParsed.Control.Properties,
                        MatrixName: matrixNameDialogResult.Text
                    }
                }
            })
        };

        this.moduleService.moduleContent = null;

        await this.applyPatch([{
            Operation: 'Upsert',
            Parameters: {
                Value: matrixNameDialogResult.Text,
                PropertyName: 'MatrixName'
            },
            PropertyPaths: [[
                ...this.moduleService.fieldsPath,
                field.FieldId,
                'Control',
                'Properties'
            ]]
        }]);

        await this.moduleService.updateField(
            newField,
            this.tenantService.selectedTenant,
            this.moduleId,
            this.version,
            this.stepId
        ).toPromise();
    }


    public async exposeField(field: Field): Promise<void> {
        const dialogRef =
          this.dialog.open<PopupDialog, {}, DialogResult>(PopupDialog);
        const dialogInstance = dialogRef.componentInstance;
        dialogInstance.DialogTitle = 'You are about to unprotect a PII field';
        dialogInstance.DialogDescription =
          '<div> This might expose PII saved within this field to<br> users within this tenant<br>Are you sure? </div>';
        dialogInstance.DialogConfirmButtonText = 'Yes';
        dialogInstance.DialogCancelButtonText = 'Go Back';

        const protectedStatusDialogResult = await dialogRef
          .afterClosed()
          .toPromise();

        if (
          !protectedStatusDialogResult ||
          !protectedStatusDialogResult.Result
        ) {
          return;
        }

        this.toggleProtectedStatus(field);
    }

    public async setTextBoxDefaultValue(field: Field): Promise<void> {
        const dialogRef = this.dialog.open<PopupDialog, {}, DialogResult>(PopupDialog);
        const dialogInstance = dialogRef.componentInstance;
        dialogInstance.DialogTitle = `Set Default Value - ${this.prettifyLabel(field.FieldName)}`;
        dialogInstance.DialogConfirmButtonText = 'Save';
        dialogInstance.DialogCancelButtonText = 'Cancel';
        dialogInstance.DisplayTextBox = true;
        dialogInstance.DialogPlaceHolder = 'Enter text here';

        const fieldParsed = JSON.parse(field.Json);
        const jsonDefaultValue = fieldParsed.Control.DefaultValue;
        if (jsonDefaultValue) {
            dialogInstance.DialogTextBox = jsonDefaultValue.Value;
        }
        this.configureApplyEvenWhenHiddenCheckbox(dialogInstance, jsonDefaultValue);

        const setDefaultDialogResult = await dialogRef.afterClosed().toPromise();
        if (!setDefaultDialogResult || !setDefaultDialogResult.Result) {
            return;
        }

        const defaultValue = {
            ControlPropertyName: 'TextValue',
            Value: setDefaultDialogResult.Text,
            ApplyDefaultEvenWhenHidden: this.getApplyEvenWhenHidden(setDefaultDialogResult)
        };

        await this.updateDefaultValue(defaultValue, field, fieldParsed);
    }

    private configureApplyEvenWhenHiddenCheckbox(dialogInstance: PopupDialog, jsonDefaultValue: any): void {
        dialogInstance.DisplayCheckbox = true;
        dialogInstance.DialogCheckboxLabel = 'Apply Even When Hidden';
        dialogInstance.DialogCheckboxChecked = jsonDefaultValue?.ApplyDefaultEvenWhenHidden ?? false;
    }

    private getApplyEvenWhenHidden(dialogResult: DialogResult): boolean {
        return dialogResult.Payload?.applyEvenWhenHidden ?? false;
    }

    public getDynamicDefaultValueTokenOptions(field: Field): DynamicDefaultValueTokenOption[] {
        return getSupportedDynamicDefaultValueTokens(field);
    }

    public getSelectedDynamicDefaultValueToken(fieldPropertyName: string): string {
        const existingDefault = this.moduleService.selectedStep?.DefaultDataValues?.find(
            (defaultDataValue: DefaultDataValue) => defaultDataValue.OnChangePropertyName === fieldPropertyName
        );

        return getSelectedDynamicDefaultValueToken(existingDefault)?.token;
    }

    public async openDynamicDefaultValueDialog(field: Field): Promise<string | null | undefined> {
        const options = this.getDynamicDefaultValueTokenOptions(field);
        if (!options.length) {
            return undefined;
        }

        const dialogRef = this.dialog.open<PopupDialog, {}, DialogResult>(PopupDialog);
        const dialogInstance = dialogRef.componentInstance;
        dialogInstance.DialogTitle = `Set Default Value - ${this.prettifyLabel(field.FieldName)}`;
        dialogInstance.DialogConfirmButtonText = 'Save';
        dialogInstance.DialogCancelButtonText = 'Cancel';
        dialogInstance.DialogOptions = options.map(option => option.label);

        const selectedToken = this.getSelectedDynamicDefaultValueToken(field.FieldId);
        if (selectedToken) {
            dialogInstance.DialogSelectedOption = options.find(option => option.token === selectedToken)?.label;
        }

        const dialogResult = await dialogRef.afterClosed().toPromise();
        if (!dialogResult?.Result) {
            return undefined;
        }

        if (dialogResult.Payload?.clearSelection) {
            return null;
        }

        return options.find(option => option.label === dialogResult.Text)?.token;
    }

    public async setDynamicDefaultValue(field: Field): Promise<void> {
        const selectedToken = await this.openDynamicDefaultValueDialog(field);
        if (selectedToken === undefined) {
            return;
        }

        const remainingDefaults = (this.moduleService.selectedStep.DefaultDataValues ?? []).filter(
            (defaultDataValue: DefaultDataValue) => defaultDataValue.OnChangePropertyName !== field.FieldId
                || !getSelectedDynamicDefaultValueToken(defaultDataValue)
        );

        const defaultDataValues = selectedToken === null
            ? remainingDefaults
            : [...remainingDefaults, buildDynamicDefaultDataValue(field, selectedToken)];

        await this.moduleService.updateDefaultDataValues(
            defaultDataValues,
            this.tenantService.selectedTenant,
            this.moduleId,
            this.version,
            this.stepId
        ).toPromise();
    }

    public async setListDefaultValue(field: Field): Promise<void> {
        if (this.getDynamicDefaultValueTokenOptions(field).length) {
            await this.setDynamicDefaultValue(field);
            return;
        }

        const items = await this.getListItems(field.ListAlias);
        const itemsNames = this.getListItemsName(items);

        const dialogRef = this.dialog.open<PopupDialog, {}, DialogResult>(PopupDialog);
        const dialogInstance = dialogRef.componentInstance;
        dialogInstance.DialogTitle = `Set Default Value - ${this.prettifyLabel(field.FieldName)}`;
        dialogInstance.DialogConfirmButtonText = 'Save';
        dialogInstance.DialogCancelButtonText = 'Cancel';
        dialogInstance.DialogOptions = itemsNames;

        const fieldParsed = JSON.parse(field.Json);
        const jsonDefaultValue = fieldParsed.Control.DefaultValue;
        if (jsonDefaultValue) {
            const selectedItem = items.find((item) => item.Value === jsonDefaultValue.Value);
            const selectedItemValue = selectedItem !== undefined ? selectedItem.Text : undefined;

            dialogInstance.DialogSelectedOption = selectedItemValue;
        }
        this.configureApplyEvenWhenHiddenCheckbox(dialogInstance, jsonDefaultValue);

        const setDefaultDialogResult = await dialogRef.afterClosed().toPromise();
        if (!setDefaultDialogResult || !setDefaultDialogResult.Result) {
            return;
        }

        if (setDefaultDialogResult.Payload && setDefaultDialogResult.Payload.clearSelection) {
            await this.updateDefaultValue(null, field, fieldParsed);
            return;
        }

        const itemAlias = items.find((item) => item.Text === setDefaultDialogResult.Text);
        if (!itemAlias) {
            return;
        }

        const defaultValue = {
            ControlPropertyName: 'SelectedItemAsString',
            Value: itemAlias.Value,
            ApplyDefaultEvenWhenHidden: this.getApplyEvenWhenHidden(setDefaultDialogResult)
        };

        await this.updateDefaultValue(defaultValue, field, fieldParsed);
    }

    public async setCheckBoxDefaultValue(field: Field): Promise<void> {
        const dialogRef = this.dialog.open<PopupDialog, {}, DialogResult>(PopupDialog);
        const dialogInstance = dialogRef.componentInstance;
        dialogInstance.DialogTitle = `Set Default Value - ${this.prettifyLabel(field.FieldName)}`;
        dialogInstance.DialogConfirmButtonText = 'Save';
        dialogInstance.DialogCancelButtonText = 'Cancel';
        dialogInstance.DialogOptions = ['Yes', 'No'];

        const fieldParsed = JSON.parse(field.Json);
        const jsonDefaultValue = fieldParsed.Control.DefaultValue;
        if (jsonDefaultValue) {
            dialogInstance.DialogSelectedOption = jsonDefaultValue.Value ? 'Yes' : 'No';
        }
        this.configureApplyEvenWhenHiddenCheckbox(dialogInstance, jsonDefaultValue);

        const setDefaultDialogResult = await dialogRef.afterClosed().toPromise();
        if (!setDefaultDialogResult || !setDefaultDialogResult.Result) {
            return;
        }

        if (setDefaultDialogResult.Payload && setDefaultDialogResult.Payload.clearSelection) {
            await this.updateDefaultValue(null, field, fieldParsed);
            return;
        }

        const defaultValue = {
            ControlPropertyName: 'Checked',
            Value: setDefaultDialogResult.Text === 'Yes',
            ApplyDefaultEvenWhenHidden: this.getApplyEvenWhenHidden(setDefaultDialogResult)
        };

        await this.updateDefaultValue(defaultValue, field, fieldParsed);
    }

    public async editJson(field: Field): Promise<void> {
        const baseJson = JSON.parse(field.Json);
        const dialogRef = this.dialog.open<JsonEditorComponent, {}, DialogResult>(JsonEditorComponent);
        const dialogInstance = dialogRef.componentInstance;
        dialogInstance.dialogTitle = `Edit Field - ${this.prettifyLabel(field.FieldName)}`;
        dialogInstance.dialogCodeArea = JSON.stringify(baseJson, undefined, 4);

        if (this.canEditJson) {
            dialogInstance.extraValidator = (parsedJson) => {
                const conflict = findFieldAliasConflict(parsedJson, field, {
                    fields: this.allFieldsAcrossSteps(),
                    hierarchyAttributes: this.hierarchyAttributes
                });
                return conflict
                    ? `"${conflict}" matches an existing field ID or hierarchy attribute in this form flow. Update the property name to avoid duplicates.`
                    : null;
            };
        } else {
            dialogInstance.readonly = true;
            dialogInstance.dialogTitle = `Field - ${this.prettifyLabel(field.FieldName)}`;
        }

        const dialogResult = await dialogRef.afterClosed().toPromise();

        if (!dialogResult || !dialogResult.Result) {
            return;
        }

        const modifiedJson = JSON.parse(dialogResult.Text);

        field.Json = dialogResult.Text;
        field.Modified = true;
        field.Changes = this.getJsonChanges(baseJson, modifiedJson);
        field.ReasonForChange = dialogResult.Payload;

        this.moduleService.moduleContent = null;
        await this.moduleService.updateFieldJson(
            field,
            this.tenantService.selectedTenant,
            this.moduleId,
            this.version,
            this.stepId,
        ).toPromise();

        await this.applyPatch([{
            Operation: 'Replace',
            Parameters: {
                Value: JSON.parse(dialogResult.Text)
            },
            PropertyPaths: [[
                ...this.moduleService.fieldsPath,
                field.FieldId
            ]]
        }]);
    }

    public displaySetDefaultValueForList(field: Field): boolean {
        return this.getDynamicDefaultValueTokenOptions(field).length > 0 || field.FieldUniqueId === FormFlowFieldTypes.List &&
            (field.IsCustomField || !field.IsMandatory) &&
            (field.ListType === ListSelectTypes.SingleDropDown ||
                field.ListType === ListSelectTypes.JumboRadio ||
                field.ListType === ListSelectTypes.SingleRadios) &&
            field.ListValueType === ListTypes.AirswebList;
    }

    public async reorderField(uiGroupName: string, fieldToReorder: Field, direction: ReorderingDirection): Promise<void> {
        const groupItems = this.getOrderedGroupItems(uiGroupName);

        const currentIndexInGroup = groupItems.findIndex(field => field.FieldId === fieldToReorder.FieldId);
        if (direction === ReorderingDirection.Up && currentIndexInGroup === 0) {
            return; // Already at the top
        }
        if (direction === ReorderingDirection.Down && currentIndexInGroup === groupItems.length - 1) {
            return; // Already at the bottom
        }

        const fieldToSwapWith = direction === ReorderingDirection.Up ?
            groupItems[(currentIndexInGroup + groupItems.length - 1) % groupItems.length] :
            groupItems[(currentIndexInGroup + 1) % groupItems.length];

        fieldToReorder.Modified = true;
        fieldToSwapWith.Modified = true;
        this.switchFieldPosition(fieldToSwapWith, fieldToReorder, direction);

        await this.applyPatch([{
            Operation: 'Swap',
            PropertyPaths: [[... this.moduleService.fieldsPath, fieldToReorder.FieldId, 'Order'],
            [... this.moduleService.fieldsPath, fieldToSwapWith.FieldId, 'Order']]
        }]);

        this.updateOrder(fieldToReorder, fieldToSwapWith);
    }

    public switchFieldPosition(source: Field, destination: Field, direction: ReorderingDirection): void {
        let srcOrder = source.Order;
        const destOrder = destination.Order;

        if (srcOrder === destOrder) {
            if (direction === ReorderingDirection.Up) {
                srcOrder--;
            } else {
                srcOrder++;
            }
        }

        const fieldInObservable = this.moduleService.fields.find(field => field.FieldId === source.FieldId);

        fieldInObservable.Order = destOrder;
        destination.Order = srcOrder;
    }

    public getOrderedGroupItems(uiGroupName: string): Field[] {
        const regex = new RegExp(`(${uiGroupName})`, 'i');
        const groupItems = this.moduleService.fields
            .filter(field => field.UiGroupName === uiGroupName && (!this.fieldFilter || regex.test(field.FieldName)));
        return groupItems.sort((a: Field, b: Field) => {
            if (a.Order < b.Order) {
                return -1;
            }

            if (a.Order > b.Order) {
                return 1;
            }

            return 0;
        });
    }

    public editTreeConfiguration(field: Field): void {
        this.router.navigate(['tree-configuration', this.stepId, field.FieldId], {
            relativeTo: this.getChildRoute(this.activatedRoute.root, 4),
        });
    }

    public async toggleAlwaysHidden(field: Field): Promise<void> {
        field.IsAlwaysHidden = !field.IsAlwaysHidden;

        if (!field.IsCustomField) {
            field.Modified = true;
        }

        await this.moduleService.updateField(
            field,
            this.tenantService.selectedTenant,
            this.moduleId,
            this.version,
            this.stepId
        ).toPromise();

        await this.applyPatch([{
            Operation: 'Upsert',
            Parameters: {
                Value: field.IsAlwaysHidden,
                PropertyName: 'AlwaysHidden'
            },
            PropertyPaths: [[
                ...this.moduleService.fieldsPath,
                field.FieldId,
                'Control',
                'Properties'
            ]]
        }]);
    }

    public async toggleHideOnUdq(field: Field): Promise<void> {
        field.HideOnUdq = !field.HideOnUdq;

        if (!field.IsCustomField) {
            field.Modified = true;
        }

        await this.moduleService.updateField(
            field,
            this.tenantService.selectedTenant,
            this.moduleId,
            this.version,
            this.stepId
        ).toPromise();

        await this.applyPatch([{
            Operation: 'Upsert',
            Parameters: {
                Value: field.HideOnUdq,
                PropertyName: 'HideOnUdq'
            },
            PropertyPaths: [[
                ...this.moduleService.fieldsPath,
                field.FieldId
            ]]
        }]);
    }

    public async toggleCascadeChanges(field: Field): Promise<void> {
        field.CascadeChanges = !field.CascadeChanges;

        if (!field.IsCustomField) {
            field.Modified = true;
        }

        await this.moduleService.updateField(
            field,
            this.tenantService.selectedTenant,
            this.moduleId,
            this.version,
            this.stepId
        ).toPromise();

        await this.applyPatch([{
            Operation: 'Upsert',
            Parameters: {
                Value: field.CascadeChanges,
                PropertyName: 'CascadeChanges'
            },
            PropertyPaths: [[
                ...this.moduleService.fieldsPath,
                field.FieldId
            ]]
        }]);
    }

    public async toggleIncludeInSwift(field: Field): Promise<void> {
        field.IncludeInSwift = !field.IncludeInSwift;

        if (!field.IsCustomField) {
            field.Modified = true;
        }

        await this.moduleService.updateField(
            field,
            this.tenantService.selectedTenant,
            this.moduleId,
            this.version,
            this.stepId
        ).toPromise();

        await this.applyPatch([{
            Operation: 'Upsert',
            Parameters: {
                Value: field.IncludeInSwift,
                PropertyName: 'IncludeInSwift'
            },
            PropertyPaths: [[
                ...this.moduleService.fieldsPath,
                field.FieldId
            ]]
        }]);
    }

    public async toggleProtectedStatus(field: Field): Promise<void> {
        field.IsProtected = !field.IsProtected;

        if (!field.IsCustomField) {
            field.Modified = true;
        }

        await this.moduleService.updateField(
            field,
            this.tenantService.selectedTenant,
            this.moduleId,
            this.version,
            this.stepId
        ).toPromise();

        await this.applyPatch([{
            Operation: 'Upsert',
            Parameters: {
                Value: field.IsProtected,
                PropertyName: 'IsProtected'
            },
            PropertyPaths: [[
                ...this.moduleService.fieldsPath,
                field.FieldId
            ]]
        }]);
    }

    public isPiiEnabledModule(): boolean {
        return this.moduleId === ModuleUniqueIds.Events || this.moduleId === ModuleUniqueIds.People;
    }

    public getQuickReportTooltipText(field: Field): string {
        if (field.IsMandatory) {
            return 'Mandatory fields must be included in quick report';
        }
        else if (field.FieldUniqueId === FormFlowFieldTypes.LinkedRecord) {
            return 'Linked record fields cannot be included in quick report';
        }
        return field.IncludeInSwift ? 'Exclude from quick report' : 'Include in quick report';
    }

    public isQuickReportToggleEnabled(field: Field): boolean {
        return !(field.FieldUniqueId === FormFlowFieldTypes.LinkedRecord || field.IsMandatory);
    }

    private getJsonChanges(baseObject, modifiedObject): JsonChange[] {
        const modifiedProperties = this.getModifiedProperties(baseObject, modifiedObject);
        const newProperties = this.getModifiedNewProperties(baseObject, modifiedObject);
        return [...modifiedProperties, ...newProperties];
    }

    private getModifiedProperties(baseObject, modifiedObject, fieldPath = ''): JsonChange[] {
        const changes: JsonChange[] = [];
        for (const key of Object.keys(baseObject)) {
            const baseValue = baseObject[key];
            const modifiedValue = modifiedObject[key];
            if (this.isObject(baseValue) && this.isObject(modifiedValue)) {
                const path = `${fieldPath}${key}:`;
                const otherChanges = this.getModifiedProperties(baseValue, modifiedValue, path);
                changes.push(...otherChanges);
            }
            else if (baseValue !== modifiedValue) {
                const change = new JsonChange(
                    `${fieldPath}${key}`,
                    JSON.stringify(baseValue),
                    JSON.stringify(modifiedValue)
                );
                changes.push(change);
            }
        }
        return changes;
    }

    private getModifiedNewProperties(baseObject, modifiedObject, fieldPath = ''): JsonChange[] {
        const changes: JsonChange[] = [];
        const baseObjectKeys = Object.keys(baseObject);
        const modifiedObjectKeys = Object.keys(modifiedObject);
        for (const key of modifiedObjectKeys) {
            if (!baseObjectKeys.includes(key)) {
                const change = new JsonChange(
                    `${fieldPath}${key}`,
                    JSON.stringify(undefined),
                    JSON.stringify(modifiedObject[key])
                );
                changes.push(change);
                continue;
            }
            if (this.isObject(baseObject[key]) && this.isObject(modifiedObject[key])) {
                const path = `${fieldPath}${key}:`;
                const otherChanges = this.getModifiedNewProperties(baseObject[key], modifiedObject[key], path);
                changes.push(...otherChanges);
            }
        }
        return changes;
    }

    private isObject(value: any): boolean {
        return typeof value === 'object' && value !== null;
    }

    private *allFieldsAcrossSteps(): Iterable<Field> {
        const steps = this.moduleService.moduleContent?.Steps ?? [];
        for (const step of steps) {
            for (const f of step.Fields ?? []) {
                yield f;
            }
        }
    }

    private addFieldGroupToCurrentFieldGroupsForSelectedStep(fieldGroupAliasWithModulePrefix: string): void {
        const newFieldGroup: FieldGroup = {
            GroupName: fieldGroupAliasWithModulePrefix,
            UiGroupName: fieldGroupAliasWithModulePrefix
        };

        this.moduleService.addGroup(newFieldGroup);
        this.newGroupName = '';
        this.addNewGroupIsDisabled = true;
    }

    private removeModulePrefixFromGroupAlias(fieldGroupAlias: string): string {
        const groupListItemPrefixWithColon = this.groupListPrefix + ':';
        return fieldGroupAlias.slice(
            fieldGroupAlias.indexOf(groupListItemPrefixWithColon) + groupListItemPrefixWithColon.length, fieldGroupAlias.length);
    }

    private validateNewGroup(): boolean {
        if (!this.newGroupName || this.newGroupName.trim().length <= 0) {
            this.newGroupErrorString = 'Group Name cannot be blank.';
            this.addNewGroupIsDisabled = true;
            return false;
        }

        this.newGroupErrorString = '';
        this.addNewGroupIsDisabled = false;
        return true;
    }

    private fieldGroupsAlreadyContainsNewGroup(newGroupNameAliasWithModulePrefix: string): boolean {
        return this.moduleService.fieldGroups
            .filter(fieldGroup => fieldGroup.GroupName === newGroupNameAliasWithModulePrefix).length > 0;
    }

    private getListItems(listAlias: string): Promise<any[]> {
        const listItems = this.tenantService.getListItems([ListTypes.AirswebList, listAlias]);
        return listItems.toPromise();
    }

    private getListItemsName(itemList: any[]): string[] {
        const itemsNames = new Array<string>();
        itemList.forEach(item => {
            itemsNames.push(item.Text);
        });

        return itemsNames;
    }

    private async updateDefaultValue(defaultValue: any, field: Field, fieldParsed: any): Promise<void> {
        const newField = {
            ...field,
            Modified: true,
            Json: JSON.stringify({
                ...fieldParsed,
                Control: {
                    ...fieldParsed.Control,
                    DefaultValue: defaultValue
                }
            })
        };

        await this.moduleService.updateFieldJson(
            newField,
            this.tenantService.selectedTenant,
            this.moduleId,
            this.version,
            this.stepId,
        ).toPromise();

        await this.applyPatch([{
            Operation: 'Upsert',
            Parameters: {
                Value: defaultValue,
                PropertyName: 'DefaultValue'
            },
            PropertyPaths: [[
                ...this.moduleService.fieldsPath,
                field.FieldId,
                'Control'
            ]]
        }]);
    }

    private applyPatch(patch: Patch[]): Promise<any> {
        return this.moduleService.applyPatch(this.tenantService.selectedTenant, this.moduleId, patch);
    }

    private moveMemberInArray(array: any[], oldIndex: number, newIndex: number): void {
        const removedElement = array.splice(oldIndex, 1)[0];
        if (removedElement) {
            array.splice(newIndex, 0, removedElement);
        }
    }

    private fieldToPatch(field: Field): Patch {
        return {
            Operation: 'Upsert',
            Parameters: {
                Value: field.Order,
                PropertyName: 'Order'
            },
            PropertyPaths: [[
                ...this.moduleService.fieldsPath,
                field.FieldId
            ]]
        } as Patch;
    }

    private reevaluateOrderOfFieldArray(groupedFields: Field[]): void {
        for (let i = 0; i < groupedFields.length; i++) {
            const field = groupedFields[i];
            if (field) {
                groupedFields[i].Order = i + 1;
            }
        }
    }
}

enum ReorderingDirection {
    Up = 0,
    Down = 1
}
